import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, PublicContainer } from '@mts/ui';
import { fetchListingBySlug } from '../services/api';
import {
  approveAutomatedClaim,
  continueClaimManualReview,
  fetchClaimPhoneOptions,
  requestClaimPhoneCode,
  reserveFreeClaimPromotion,
  verifyClaimPhoneCode,
} from '../services/claimFlow';
import { formatPhoneNumber } from '../utils/phone';
import { redirectToPortalWithHandoff } from '../utils/portalUrls';

function normalizeText(value) {
  return String(value || '').trim();
}

function buildListingAddress(listing) {
  const explicitAddress = normalizeText(listing?.address);
  if (explicitAddress) return explicitAddress;
  return [
    listing?.addressLine1,
    listing?.addressLine2,
    listing?.city,
    listing?.state,
    listing?.postalCode,
  ].map(normalizeText).filter(Boolean).join(', ');
}

function getListingHref({ categorySlug, slug, listing }) {
  const publicPath = normalizeText(listing?.publicUrl || listing?.publicPath);
  if (publicPath.startsWith('/') && !publicPath.startsWith('//')) {
    return publicPath.replace(/\/claim(?:\/.*)?$/, '');
  }
  if (categorySlug && slug) return `/listings/${encodeURIComponent(categorySlug)}/${encodeURIComponent(slug)}`;
  if (slug) return `/listings/${encodeURIComponent(slug)}`;
  return '/listings';
}

export default function ClaimAutomationPage({ mode = 'auto' }) {
  const toast = useFeedback();
  const { categorySlug = '', slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const claimToken = searchParams.get('claimToken') || '';
  const promotionCode = searchParams.get('promo') || sessionStorage.getItem('mtsPromotionCode') || '';
  const selectedPriceId = searchParams.get('priceId') || '';
  useEffect(() => { if (promotionCode) sessionStorage.setItem('mtsPromotionCode', promotionCode); }, [promotionCode]);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(mode === 'auto');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [phoneOptions, setPhoneOptions] = useState(null);
  const [verification, setVerification] = useState(null);
  const [code, setCode] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  useEffect(() => {
    let ignore = false;

    async function loadListing() {
      setLoading(true);
      setError('');
      try {
        const payload = await fetchListingBySlug(slug, { categorySlug });
        if (!ignore) setListing(payload || null);
      } catch (err) {
        if (!ignore) setError(err?.message || 'Unable to load this listing.');
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    if (!slug) {
      setError('Listing slug is required.');
      setLoading(false);
      return undefined;
    }

    loadListing();
    return () => {
      ignore = true;
    };
  }, [categorySlug, slug]);

  const listingId = listing?.id || slug;
  const listingHref = useMemo(() => getListingHref({ categorySlug, slug, listing }), [categorySlug, slug, listing]);
  const listingName = normalizeText(listing?.name) || 'this business';
  const listingAddress = buildListingAddress(listing);
  const listingPhone = normalizeText(listing?.phone || listing?.phoneNumber);
  const finishClaim = (fallback) => {
    if (promotionCode || selectedPriceId) {
      const params = new URLSearchParams();
      if (promotionCode) params.set('promo', promotionCode);
      if (selectedPriceId) params.set('priceId', selectedPriceId);
      redirectToPortalWithHandoff(navigate, `/manage/listings/${encodeURIComponent(listingId)}/upgrade?${params.toString()}`, { replace: true });
      return;
    }
    navigate(fallback || listingHref, { replace: true });
  };

  useEffect(() => {
    if (mode !== 'auto' || loading || !listing) return undefined;
    let cancelled = false;

    async function approveClaim() {
      if (!claimToken) {
        setWorking(false);
        setError('This claim link is missing a verification token.');
        return;
      }

      setWorking(true);
      setError('');
      try {
        const promotionReservation = await reserveFreeClaimPromotion(listingId);
        const payload = await approveAutomatedClaim(listingId, { claimToken, ...promotionReservation });
        if (cancelled) return;
        setMessage('Your claim has been approved.');
        window.setTimeout(() => {
          finishClaim(payload?.redirectTo);
        }, 1200);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Unable to approve this claim.');
      } finally {
        if (!cancelled) setWorking(false);
      }
    }

    approveClaim();
    return () => {
      cancelled = true;
    };
  }, [claimToken, listing, listingHref, listingId, loading, mode, navigate, promotionCode, selectedPriceId]);

  useEffect(() => {
    const target = verification?.resendAvailableAt ? new Date(verification.resendAvailableAt).getTime() : 0;
    if (!target) {
      setResendSeconds(0);
      return undefined;
    }
    const update = () => setResendSeconds(Math.max(0, Math.ceil((target - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [verification?.resendAvailableAt]);

  useEffect(() => {
    if (mode !== 'phone' || loading || !listing || !claimToken) return undefined;
    let cancelled = false;
    fetchClaimPhoneOptions(listingId, claimToken)
      .then((payload) => { if (!cancelled) setPhoneOptions(payload); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Unable to load phone verification options.'); });
    return () => { cancelled = true; };
  }, [claimToken, listing, listingId, loading, mode]);

  const handleCodeRequest = async (channel) => {
    setWorking(true);
    setError('');
    setMessage('');
    try {
      const payload = await requestClaimPhoneCode(listingId, { claimToken, channel, smsConsent: channel === 'sms' ? smsConsent : undefined });
      setVerification(payload?.verification || null);
      setCode('');
      toast.success(channel === 'sms' ? 'A verification code was texted to the listing number.' : 'The listing number is being called with a verification code.', { id: 'action:ClaimAutomationPage:handleCodeRequest' + ':' + (listingId || '') });
    } catch (err) {
      toast.error(err?.message || 'Unable to send a verification code.', { id: 'action:ClaimAutomationPage:handleCodeRequest' + ':' + (listingId || '') });
    } finally {
      setWorking(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    setWorking(true);
    setError('');
    try {
      const promotionReservation = await reserveFreeClaimPromotion(listingId);
      const payload = await verifyClaimPhoneCode(listingId, { claimToken, verificationId: verification?.verificationId, code, ...promotionReservation });
      toast.success('Your phone number was verified and your claim has been approved.', { id: 'action:ClaimAutomationPage:handleVerifyCode' + ':' + (listingId || '') });
      window.setTimeout(() => finishClaim(payload?.redirectTo), 1000);
    } catch (err) {
      toast.error(err?.message || 'Unable to verify that code.', { id: 'action:ClaimAutomationPage:handleVerifyCode' + ':' + (listingId || '') });
    } finally {
      setWorking(false);
    }
  };

  const handleManualReview = async () => {
    setWorking(true);
    setError('');
    setMessage('');
    try {
      await continueClaimManualReview(listingId, claimToken);
      toast.success('Your claim will be manually reviewed.', { id: 'action:ClaimAutomationPage:handleManualReview' + ':' + (listingId || '') });
    } catch (err) {
      toast.error(err?.message || 'Unable to continue with manual review.', { id: 'action:ClaimAutomationPage:handleManualReview' + ':' + (listingId || '') });
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <section className="py-8 sm:py-12">
        <PublicContainer>
          <Card className="flex min-h-[18rem] items-center justify-center p-6">
            <span className="loading loading-spinner loading-lg" aria-label="Loading claim verification" />
          </Card>
        </PublicContainer>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-12">
      <PublicContainer>
        <Card className="mx-auto max-w-2xl space-y-6 p-6">
          <div className="space-y-2">
            <Link className="text-sm font-medium text-primary hover:text-primary-hover" to={listingHref}>
              Back to listing
            </Link>
            <h1 className="text-3xl font-semibold tracking-tight text-text-primary">
              {mode === 'auto' ? 'Finishing your claim' : 'Verify by phone'}
            </h1>
            <p className="text-sm text-text-secondary">{listingName}</p>
          </div>

          <div className="divide-y divide-border rounded-lg border border-border">
            <div className="p-4">
              <p className="text-xs font-semibold uppercase text-text-secondary">Address</p>
              <p className="mt-1 text-sm text-text-primary">{listingAddress || 'Not provided'}</p>
            </div>
            <div className="p-4">
              <p className="text-xs font-semibold uppercase text-text-secondary">Listing phone</p>
              <p className="mt-1 text-sm text-text-primary">{formatPhoneNumber(listingPhone) || 'Not provided'}</p>
            </div>
          </div>

          {working ? (
            <div className="flex items-center gap-3 rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-text-secondary">
              <span className="loading loading-spinner loading-sm" aria-hidden="true" />
              {mode === 'auto' ? 'Approving your claim...' : 'Updating your claim...'}
            </div>
          ) : null}

          {error ? (
            <InlineFeedback variant="error" className="rounded-md border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">{error}</InlineFeedback>
          ) : null}

          {message ? (
            <InlineFeedback variant="status" className="rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">{message}</InlineFeedback>
          ) : null}

          {mode === 'phone' ? (
            <div className="space-y-5">
              <p className="text-sm text-text-secondary">
                Send a six-digit code to {phoneOptions?.maskedDestination || 'the published listing number'}.
              </p>
              {phoneOptions?.sms ? (
                <label className="flex items-start gap-2 text-sm text-text-secondary">
                  <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={smsConsent} onChange={(event) => setSmsConsent(event.target.checked)} />
                  <span>I am authorized to receive one transactional verification text at this business number.</span>
                </label>
              ) : null}
              <div className="flex flex-col gap-3 sm:flex-row">
                {phoneOptions?.sms ? (
                  <Button type="button" variant="primary" loading={working} disabled={working || resendSeconds > 0 || !claimToken || !smsConsent} onClick={() => handleCodeRequest('sms')}>
                    Text code
                  </Button>
                ) : null}
                {phoneOptions?.voice ? (
                  <Button type="button" variant={phoneOptions?.sms ? 'outline' : 'primary'} loading={working} disabled={working || resendSeconds > 0 || !claimToken} onClick={() => handleCodeRequest('voice')}>
                    Call with code
                  </Button>
                ) : null}
              </div>
              {resendSeconds > 0 ? <p className="text-xs text-text-secondary">You can request another code in {resendSeconds} seconds.</p> : null}
              {phoneOptions && !phoneOptions.sms && !phoneOptions.voice ? (
                <p className="text-sm text-warning">Automated phone verification is unavailable. Continue with manual review.</p>
              ) : null}
              {verification ? (
                <form className="space-y-3 rounded-lg border border-border p-4" onSubmit={handleVerifyCode}>
                  <label className="block text-sm font-semibold text-text-primary" htmlFor="claim-phone-code">Verification code</label>
                  <input
                    id="claim-phone-code"
                    className="input input-bordered w-full"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    required
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                  <Button type="submit" variant="primary" loading={working} disabled={working || code.length !== 6}>Verify and approve claim</Button>
                </form>
              ) : null}
              <Button type="button" variant="outline" disabled={working || !claimToken} onClick={handleManualReview}>
                Continue with manual review
              </Button>
            </div>
          ) : null}
        </Card>
      </PublicContainer>
    </section>
  );
}
